interface SelectOption {
  value: string;
  label: string;
}

interface GrossingMetadata {
  sourceId?: string | null;
  subsiteId?: string | null;
  subsiteName?: string | null;
  collectionMethod?: string | null;
  findings?: string | null;
  material?: string | null;
}

interface LiveSpecimen {
  id: string;
  source?: { id?: string | null; name?: string | null } | null;
  sourceId?: string | null;
  sourceName?: string | null;
  subsite?: { name?: string | null } | null;
  collectionMethod?: string | null;
  findings?: string | null;
  material?: string | null;
}

interface ProjectionInput<TSpecimen extends LiveSpecimen> {
  specimens: TSpecimen[];
  grossingDataMap: Map<string, GrossingMetadata>;
  sources: SelectOption[];
  subsitesBySource: Record<string, SelectOption[]>;
}

function resolveOptionLabel(
  options: SelectOption[],
  selectedId: string | null | undefined,
  fallback: string | null | undefined
): string | null {
  if (selectedId === null) return null;
  if (!selectedId) return fallback ?? null;
  return (
    options.find(({ value }) => value === selectedId)?.label ?? fallback ?? null
  );
}

function resolveDraftValue<T>(
  draftValue: T | null | undefined,
  persistedValue: T | null | undefined
): T | null {
  return draftValue !== undefined ? draftValue : persistedValue ?? null;
}

export function projectLiveGrossingSpecimens<TSpecimen extends LiveSpecimen>({
  specimens,
  grossingDataMap,
  sources,
  subsitesBySource,
}: ProjectionInput<TSpecimen>) {
  return specimens.map((specimen) => {
    const grossing = grossingDataMap.get(specimen.id);
    const sourceId = grossing?.sourceId;
    return {
      ...specimen,
      sourceId: resolveDraftValue(
        sourceId,
        specimen.source?.id ?? specimen.sourceId
      ),
      sourceName: resolveOptionLabel(
        sources,
        sourceId,
        specimen.source?.name ?? specimen.sourceName
      ),
      subsiteName:
        grossing?.subsiteName !== undefined
          ? grossing.subsiteName
          : resolveOptionLabel(
              sourceId ? subsitesBySource[sourceId] ?? [] : [],
              grossing?.subsiteId,
              specimen.subsite?.name
            ),
      collectionMethod: resolveDraftValue(
        grossing?.collectionMethod,
        specimen.collectionMethod
      ),
      findings: resolveDraftValue(grossing?.findings, specimen.findings),
      material: resolveDraftValue(grossing?.material, specimen.material),
    };
  });
}

export function diagnosisLocationForGrossingSubsiteChange(
  currentLocation: string | null,
  patch: Pick<GrossingMetadata, "subsiteId" | "subsiteName">
): string | null {
  if (patch.subsiteId === undefined) return currentLocation;
  if (patch.subsiteId === null) return null;
  return patch.subsiteName ?? null;
}
